import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { setHeyApiGlobalFilterParams } from "@/lib/hey-api-client";
import {
  FilterQueryParams,
  FilterState,
  PeriodValue,
  createDefaultFilterState,
} from "./filter.types";
import {
  buildFilterQueryParams,
  calculateDateRange,
  getComparisonOption,
  filtersAreEqual,
} from "./filter.utils";
import {
  clearPersistedFilters,
  loadPersistedFilters,
  persistFilters,
} from "./filter.storage";

/* eslint-disable react-refresh/only-export-components */

type FilterAction =
  | { type: "SET_FILTER"; payload: FilterState }
  | { type: "SET_PERIOD"; payload: PeriodValue }
  | { type: "SET_COMPARISON"; payload: FilterState["comparison"] }
  | {
      type: "SET_CUSTOM_RANGE";
      payload: { startDate: string; endDate: string } | null;
    }
  | { type: "RESET" };

interface FilterContextState {
  filter: FilterState;
  isInitialized: boolean;
}

const initialState: FilterContextState = {
  filter: createDefaultFilterState(),
  isInitialized: false,
};

const FilterContext = createContext<
  FilterContextState & {
    applyFilter: (filter: FilterState) => void;
    setPeriod: (period: PeriodValue) => void;
    setComparison: (comparison: FilterState["comparison"]) => void;
    setCustomRange: (
      range: { startDate: string; endDate: string } | null,
    ) => void;
    resetFilters: () => void;
    getFilterQueryParams: () => FilterQueryParams;
  }
>({
  ...initialState,
  applyFilter: () => {},
  setPeriod: () => {},
  setComparison: () => {},
  setCustomRange: () => {},
  resetFilters: () => {},
  getFilterQueryParams: () => buildFilterQueryParams(initialState.filter),
});

const filterReducer = (
  state: FilterContextState,
  action: FilterAction,
): FilterContextState => {
  switch (action.type) {
    case "SET_FILTER":
      return { ...state, filter: action.payload, isInitialized: true };
    case "SET_PERIOD": {
      const dateRange = calculateDateRange(action.payload);
      const nextFilter: FilterState = {
        ...state.filter,
        period: action.payload,
        dateRange,
        ...(action.payload === "custom"
          ? { customDateRange: state.filter.customDateRange }
          : { customDateRange: undefined }),
      };
      return { ...state, filter: nextFilter, isInitialized: true };
    }
    case "SET_COMPARISON":
      return {
        ...state,
        filter: { ...state.filter, comparison: action.payload },
        isInitialized: true,
      };
    case "SET_CUSTOM_RANGE": {
      if (!action.payload) {
        return {
          ...state,
          filter: { ...state.filter, customDateRange: undefined },
          isInitialized: true,
        };
      }

      const { startDate, endDate } = action.payload;
      // Criar datas no fuso horário local para evitar bug de timezone
      // "2024-12-26" deve criar 2024-12-26T00:00:00 LOCAL, não UTC
      const [startYear, startMonth, startDay] = startDate
        .split("-")
        .map(Number);
      const [endYear, endMonth, endDay] = endDate.split("-").map(Number);

      const customDateRange = {
        startDate: new Date(startYear, startMonth - 1, startDay, 0, 0, 0),
        endDate: new Date(endYear, endMonth - 1, endDay, 23, 59, 59),
      };

      return {
        ...state,
        filter: {
          ...state.filter,
          period: "custom",
          customDateRange,
          dateRange: customDateRange,
        },
        isInitialized: true,
      };
    }
    case "RESET":
      return {
        ...state,
        filter: createDefaultFilterState(),
        isInitialized: true,
      };
    default:
      return state;
  }
};

export const FilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(filterReducer, initialState);

  useEffect(() => {
    try {
      const persisted = loadPersistedFilters();
      if (persisted) {
        dispatch({ type: "SET_FILTER", payload: persisted });
      } else {
        // Fallback usando período padrão e customer_uuid do localStorage
        const fallback = createDefaultFilterState();
        dispatch({ type: "SET_FILTER", payload: fallback });
      }
    } catch (error) {
      console.error(
        "Erro ao carregar filtros persistidos, usando estado padrão:",
        error,
      );
      const fallback = createDefaultFilterState();
      dispatch({ type: "SET_FILTER", payload: fallback });
    }
  }, []);

  useEffect(() => {
    if (!state.isInitialized) {
      return;
    }

    persistFilters(state.filter);
  }, [state.filter, state.isInitialized]);

  useEffect(() => {
    const filterParams = buildFilterQueryParams(state.filter);
    setHeyApiGlobalFilterParams(filterParams);

    return () => {
      setHeyApiGlobalFilterParams(null);
    };
  }, [state.filter]);

  const applyFilter = useCallback(
    (filter: FilterState) => {
      if (filtersAreEqual(state.filter, filter)) {
        return;
      }

      const comparison = filter.comparison?.type
        ? getComparisonOption(filter.comparison.type)
        : null;

      dispatch({
        type: "SET_FILTER",
        payload: {
          ...filter,
          comparison,
        },
      });
    },
    [state.filter],
  );

  const setPeriod = (period: PeriodValue) => {
    dispatch({ type: "SET_PERIOD", payload: period });
  };

  const setComparison = (comparison: FilterState["comparison"]) => {
    if (comparison?.type) {
      dispatch({
        type: "SET_COMPARISON",
        payload: getComparisonOption(comparison.type),
      });
    } else {
      dispatch({ type: "SET_COMPARISON", payload: null });
    }
  };

  const setCustomRange = (
    range: { startDate: string; endDate: string } | null,
  ) => {
    dispatch({ type: "SET_CUSTOM_RANGE", payload: range });
  };

  const resetFilters = () => {
    clearPersistedFilters(createDefaultFilterState());
    dispatch({ type: "RESET" });
  };

  const value = useMemo(
    () => ({
      filter: state.filter,
      isInitialized: state.isInitialized,
      applyFilter,
      setPeriod,
      setComparison,
      setCustomRange,
      resetFilters,
      getFilterQueryParams: () => buildFilterQueryParams(state.filter),
    }),
    [state.filter, state.isInitialized, applyFilter],
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

export const useGlobalFilter = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error(
      "useGlobalFilter deve ser utilizado dentro de FilterProvider",
    );
  }
  return context;
};
